function readProviderFactory() {
    const factory = globalThis?.WolliMediaClient?.createWolliMediaProvider;

    return typeof factory === 'function' ? factory : null;
}

function createWolliMediaProvider(root, options = {}) {
    const factory = readProviderFactory();

    if (factory === null) {
        throw new Error(
            'WolliMedia featured media is enabled, but no browser bridge registered window.WolliMediaClient.createWolliMediaProvider. See docs/conventions/featured-media-integration.md.',
        );
    }

    return factory(root, options);
}

function isRecord(value) {
    return value !== null && typeof value === 'object';
}

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim() !== '') {
            return value;
        }
    }

    return null;
}

function normalizeSelection(detail) {
    if (!isRecord(detail)) {
        return null;
    }

    if (firstString(detail.mediaKind) !== 'image') {
        return null;
    }

    const finalUrl = firstString(detail.finalUrl);
    const hasFile = detail.file instanceof File;

    if (!hasFile && finalUrl === null) {
        return null;
    }

    return {
        mediaKind: 'image',
        finalUrl,
        filename: firstString(detail.filename, detail.file?.name, 'featured-image.webp'),
        crop: isRecord(detail.crop) ? detail.crop : null,
    };
}

function dispatchFieldChange(root) {
    root.dispatchEvent(new Event('change', { bubbles: true }));
}

function setStatus(root, message, isError = false) {
    const status = root.querySelector('[data-featured-media-status]');

    if (!status) {
        return;
    }

    status.textContent = message;
    status.dataset.featuredMediaError = isError ? 'true' : 'false';
}

function setPreview(root, url) {
    const preview = root.querySelector('[data-featured-media-preview]');
    const empty = root.querySelector('[data-featured-media-preview-empty]');

    if (!preview || !(preview instanceof HTMLImageElement)) {
        return;
    }

    if (typeof url === 'string' && url.trim() !== '') {
        preview.src = url;
        preview.hidden = false;

        if (empty) {
            empty.hidden = true;
        }

        return;
    }

    preview.removeAttribute('src');
    preview.hidden = true;

    if (empty) {
        empty.hidden = false;
    }
}

function clearProxyFile(input) {
    if (!(input instanceof HTMLInputElement)) {
        return;
    }

    try {
        const transfer = new DataTransfer();
        input.files = transfer.files;
    } catch {
        input.value = '';
    }
}

function assignProxyFile(input, file) {
    if (!(input instanceof HTMLInputElement) || !(file instanceof File)) {
        return false;
    }

    try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;

        return input.files?.length === 1;
    } catch {
        return false;
    }
}

function resolveOverlayParent(doc) {
    const viewportLayer = doc?.querySelector?.('#viewport-layer');

    if (viewportLayer instanceof HTMLElement) {
        return viewportLayer;
    }

    return doc?.body instanceof HTMLElement ? doc.body : null;
}

function buildOverlay(doc, { label, content, onClose }) {
    const overlayParent = resolveOverlayParent(doc);

    if (!(overlayParent instanceof HTMLElement)) {
        return null;
    }

    const overlay = doc.createElement('div');
    overlay.className = 'ww-featured-media-modal is-fullscreen';

    const dialog = doc.createElement('div');
    dialog.className = 'ww-featured-media-modal__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', label);

    const header = doc.createElement('div');
    header.className = 'ww-featured-media-modal__header';

    const titleEl = doc.createElement('h2');
    titleEl.className = 'f-section-header-title ww-featured-media-modal__title';
    titleEl.textContent = label;
    header.appendChild(titleEl);

    const closeButton = doc.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'f-button f-button--plain f-button--secondary f-button--icon ww-featured-media-modal__close';
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.textContent = '×';
    closeButton.addEventListener('click', () => close());
    header.appendChild(closeButton);

    const body = doc.createElement('div');
    body.className = 'ww-featured-media-modal__body';
    body.appendChild(content);

    dialog.appendChild(header);
    dialog.appendChild(body);
    overlay.appendChild(dialog);
    overlayParent.appendChild(overlay);

    const onKeyDown = (event) => {
        if (event.key !== 'Escape') {
            return;
        }

        close();
    };

    const close = () => {
        doc.removeEventListener('keydown', onKeyDown);
        overlay.remove();
        onClose?.();
    };

    overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
            close();
        }
    });

    doc.addEventListener('keydown', onKeyDown);

    return { close };
}

function mountFeaturedMediaField(root) {
    if (root.dataset.featuredMediaMounted === 'true') {
        return;
    }

    root.dataset.featuredMediaMounted = 'true';

    const providerTemplate = root.querySelector('[data-featured-media-provider-template]');
    const selectionInput = root.querySelector('[data-featured-media-selection]');
    const persistenceInput = root.querySelector('[data-featured-media-persistence]');
    const uploadProxyInput = root.querySelector('[data-featured-media-upload-proxy]');
    const openButton = root.querySelector('[data-featured-media-open]');
    const clearButton = root.querySelector('[data-featured-media-clear]');

    if (!(selectionInput instanceof HTMLInputElement)) {
        setStatus(root, 'Featured media provider is not configured.', true);
        return;
    }

    window.addEventListener('content-form:synced', (event) => {
        const url = event.detail?.featuredMedia?.url;

        if (typeof url === 'string') {
            setPreview(root, url);
        }
    });

    const applySelection = (detail, closeOverlay) => {
        const normalized = normalizeSelection(detail);

        if (normalized === null) {
            setStatus(root, 'Featured media provider did not return a usable image result.', true);
            return;
        }

        const file = detail?.file instanceof File ? detail.file : null;

        if (file) {
            if (!assignProxyFile(uploadProxyInput, file)) {
                setStatus(root, 'Featured media provider returned a file that could not be attached to the form.', true);
                return;
            }

            const objectUrl = globalThis.URL?.createObjectURL?.(file) || '';

            setPreview(root, objectUrl);
        } else {
            clearProxyFile(uploadProxyInput);
            setPreview(root, normalized.finalUrl ?? '');
        }

        selectionInput.value = JSON.stringify(normalized);

        if (persistenceInput instanceof HTMLInputElement) {
            persistenceInput.value = '';
        }

        setStatus(root, 'Featured image ready.');
        dispatchFieldChange(root);
        closeOverlay?.();
    };

    const clearSelection = () => {
        selectionInput.value = '';
        clearProxyFile(uploadProxyInput);

        if (persistenceInput instanceof HTMLInputElement) {
            persistenceInput.value = '';
        }

        setPreview(root, '');
        setStatus(root, 'Featured image cleared.');
        dispatchFieldChange(root);
    };

    const openProviderModal = () => {
        if (!(providerTemplate instanceof HTMLTemplateElement)) {
            setStatus(root, 'Featured media provider is not configured.', true);
            return;
        }

        const outerDoc = root.ownerDocument;
        const providerContainer = outerDoc.createElement('div');
        providerContainer.innerHTML = providerTemplate.innerHTML.trim();

        const providerRoot = providerContainer.querySelector('[data-wollimedia-client="the-media-provider"]');

        if (!(providerRoot instanceof HTMLElement)) {
            setStatus(root, 'Featured media provider markup could not be prepared.', true);
            return;
        }

        providerContainer.className = 'ww-featured-media-modal__content';

        let providerInstance = null;
        const fieldLabel = firstString(
            root.querySelector('.f-field-label')?.textContent,
            root.getAttribute?.('aria-label'),
            'Featured media',
        ) ?? 'Featured media';

        const overlay = buildOverlay(outerDoc, {
            label: fieldLabel,
            content: providerContainer,
            onClose: () => {
                providerRoot.removeEventListener('alpine-media-provider:change', onProviderChange);
                providerRoot.removeEventListener('alpine-media-provider:apply', onProviderApply);
                providerRoot.removeEventListener('alpine-media-provider:error', onProviderError);
                providerRoot.removeEventListener('alpine-media-provider:cancel', onProviderCancel);
                providerInstance?.destroy?.();
            },
        });

        if (overlay === null) {
            setStatus(root, 'Featured media provider could not create an overlay host.', true);
            return;
        }

        const onProviderChange = () => {
            setStatus(root, 'Featured image selected. Apply the crop to continue.');
        };

        const onProviderApply = (event) => {
            applySelection(event.detail?.payload || {}, overlay.close);
        };

        const onProviderError = (event) => {
            const message = firstString(event.detail?.error, 'Featured media provider failed.');
            setStatus(root, message ?? 'Featured media provider failed.', true);
        };

        const onProviderCancel = () => {
            overlay.close();
        };

        providerRoot.addEventListener('alpine-media-provider:change', onProviderChange);
        providerRoot.addEventListener('alpine-media-provider:apply', onProviderApply);
        providerRoot.addEventListener('alpine-media-provider:error', onProviderError);
        providerRoot.addEventListener('alpine-media-provider:cancel', onProviderCancel);

        try {
            providerInstance = createWolliMediaProvider(providerRoot);
            setStatus(root, 'Featured media provider ready.');
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Featured media provider failed to mount.';
            setStatus(root, message, true);
            overlay.close();
        }
    };

    if (clearButton) {
        clearButton.addEventListener('click', clearSelection);
    }

    if (openButton) {
        openButton.addEventListener('click', openProviderModal);
    }
}

export function registerFeaturedMediaFields() {
    document.querySelectorAll('[data-featured-media-field="true"][data-featured-media-driver="wollimedia"]').forEach((root) => {
        mountFeaturedMediaField(root);
    });
}
